import { useState, type FormEvent } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input, Field } from '@/components/ui/Field';
import { cn } from '@/lib/utils';

export const ACHIEVEMENTS_MAX_ITEMS = 4;

export interface AchievementItem {
  value: string;
  label: string;
  sublabel?: string;
}

interface Props {
  value: Record<string, unknown>;
  onChange: (v: Record<string, unknown>) => void | Promise<void>;
  submitting?: boolean;
  onCancel?: () => void;
}

function parseItems(value: Record<string, unknown>): AchievementItem[] {
  const raw = Array.isArray(value?.items) ? (value.items as AchievementItem[]) : [];
  return raw.map((it: AchievementItem) => ({
    value: typeof it.value === 'string' ? it.value : '',
    label: typeof it.label === 'string' ? it.label : '',
    sublabel: typeof it.sublabel === 'string' ? it.sublabel : '',
  }));
}

export function AchievementsEditForm({ value, onChange, submitting, onCancel }: Props) {
  const [items, setItems] = useState<AchievementItem[]>(parseItems(value));
  const [errors, setErrors] = useState<Record<number, Record<string, string>>>({});

  function patchItem(idx: number, patch: Partial<AchievementItem>) {
    setItems((cur) => cur.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  }

  function addItem() {
    if (items.length >= ACHIEVEMENTS_MAX_ITEMS) return;
    setItems((cur) => [...cur, { value: '', label: '', sublabel: '' }]);
  }

  function removeItem(idx: number) {
    setItems((cur) => cur.filter((_, i) => i !== idx));
    setErrors((cur) => {
      const next: Record<number, Record<string, string>> = {};
      for (const [k, v] of Object.entries(cur)) {
        const i = Number(k);
        if (i < idx) next[i] = v;
        else if (i > idx) next[i - 1] = v;
      }
      return next;
    });
  }

  function validate(): boolean {
    const next: Record<number, Record<string, string>> = {};
    items.forEach((it, idx) => {
      const itemErr: Record<string, string> = {};
      if (!it.value.trim()) itemErr.value = 'Value is required';
      if (!it.label.trim()) itemErr.label = 'Label is required';
      if (Object.keys(itemErr).length > 0) next[idx] = itemErr;
    });
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!validate()) return;
    onChange({ items });
  }

  const atMax = items.length >= ACHIEVEMENTS_MAX_ITEMS;

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gold-500">Achievements</p>
          <p className="mt-1 text-sm text-fg-muted">
            {items.length} of {ACHIEVEMENTS_MAX_ITEMS} cards — "What we've shipped." section
          </p>
        </div>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          leftIcon={<Plus className="h-4 w-4" />}
          onClick={addItem}
          disabled={atMax}
        >
          Add card
        </Button>
      </div>

      {atMax && (
        <p className="text-xs text-fg-subtle" role="status">
          Maximum of {ACHIEVEMENTS_MAX_ITEMS} cards reached.
        </p>
      )}

      {items.length === 0 && (
        <div className="rounded-md border border-dashed border-border bg-bg-elevated/40 px-6 py-10 text-center text-sm text-fg-subtle">
          No achievement cards yet. Click "Add card" to begin.
        </div>
      )}

      <ul className="space-y-4">
        {items.map((item, idx) => {
          const itemErr = errors[idx] ?? {};
          return (
            <li
              key={idx}
              className={cn(
                'rounded-md border bg-bg-elevated/40 p-4',
                Object.keys(itemErr).length > 0 ? 'border-danger/40' : 'border-border',
              )}
            >
              <div className="mb-3 flex items-center justify-between">
                <span className="text-xs font-medium text-fg-muted">Card {idx + 1}</span>
                <button
                  type="button"
                  aria-label={`Remove card ${idx + 1}`}
                  onClick={() => removeItem(idx)}
                  className="inline-flex h-7 items-center gap-1 rounded-md px-2 text-xs text-fg-muted hover:bg-danger/10 hover:text-danger"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  Remove
                </button>
              </div>

              <div className="grid gap-3 md:grid-cols-3">
                <Field id={`achievement-${idx}-value`} label="Value" required error={itemErr.value}>
                  <Input
                    id={`achievement-${idx}-value`}
                    value={item.value}
                    onChange={(e) => patchItem(idx, { value: e.target.value })}
                    placeholder="20+"
                  />
                </Field>
                <Field id={`achievement-${idx}-label`} label="Label" required error={itemErr.label}>
                  <Input
                    id={`achievement-${idx}-label`}
                    value={item.label}
                    onChange={(e) => patchItem(idx, { label: e.target.value })}
                    placeholder="engineers"
                  />
                </Field>
                <Field id={`achievement-${idx}-sublabel`} label="Sublabel" hint="Optional">
                  <Input
                    id={`achievement-${idx}-sublabel`}
                    value={item.sublabel ?? ''}
                    onChange={(e) => patchItem(idx, { sublabel: e.target.value })}
                    placeholder="across 5 timezones"
                  />
                </Field>
              </div>
            </li>
          );
        })}
      </ul>

      <div className="flex items-center justify-end gap-2 border-t border-border pt-4">
        {onCancel && (
          <Button type="button" variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
        )}
        <Button type="submit" loading={submitting} disabled={items.length === 0}>
          Save changes
        </Button>
      </div>
    </form>
  );
}
